import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { UploadCloud, FileSpreadsheet, FileText, Zap, Trash2, ChevronDown, Download, ArrowLeft, RotateCcw, Clock, CheckCircle2, AlertTriangle, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTERVAL_EXTS = ['csv', 'txt', 'dat', 'nem', 'nem12', 'mdf'];

const fmtMoney = (v, dec = 0) => (v == null ? '—' : '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 }));

const isIntervalFile = (name) => INTERVAL_EXTS.includes(name.split('.').pop().toLowerCase());

const windowsToState = (windows) => (windows || []).map((w) => ({ ...w, months: w.months ? w.months.join(',') : '' }));
const windowsToPayload = (windows) => windows.map((w) => ({
  period: w.period,
  days: w.days,
  start_hour: parseFloat(w.start_hour) || 0,
  end_hour: parseFloat(w.end_hour) || 0,
  months: w.months && String(w.months).trim() ? String(w.months).split(',').map((m) => parseInt(m.trim(), 10)).filter((m) => m >= 1 && m <= 12) : null,
}));

const RATE_FIELDS = [
  { key: 'supply_charge_daily', label: 'Supply ($/day)' },
  { key: 'usage_rate_flat', label: 'Flat ($/kWh)' },
  { key: 'usage_rate_peak', label: 'Peak ($/kWh)' },
  { key: 'usage_rate_shoulder', label: 'Shoulder ($/kWh)' },
  { key: 'usage_rate_off_peak', label: 'Off-Peak ($/kWh)' },
  { key: 'demand_rate', label: 'Demand ($/kW)' },
];

const StepHeader = ({ step }) => {
  const steps = ['Upload Files', 'Review Sites', 'Results'];
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="opt-step-header">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === i + 1 ? 'bg-[#015492] text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{step > i + 1 ? '✓' : i + 1}</span>
            {label}
          </div>
          {i < steps.length - 1 && <div className="w-8 h-px bg-slate-300" />}
        </div>
      ))}
    </div>
  );
};

export default function TariffOptimisation() {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [session, setSession] = useState(null);
  const [sites, setSites] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [analysing, setAnalysing] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(API_URL + '/api/tariffs?authorization=' + token)
      .then((r) => setTariffs(r.data || []))
      .catch(() => {});
  }, [token]);

  const distributors = [...new Set(tariffs.map((t) => t.network_distributor).filter(Boolean))].sort();

  const addFiles = (list) => {
    const arr = Array.from(list);
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...arr.filter((f) => !names.has(f.name))];
    });
  };

  const handleUpload = () => {
    if (!files.length) { toast.error('Add at least one NEM12/data file'); return; }
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    axios.post(API_URL + '/api/optimisation/upload?authorization=' + token, fd, { timeout: 300000 })
      .then((r) => {
        const data = r.data;
        if (!data.sites.length) { toast.error('No interval data (NEM12) sites found in the uploaded files'); return; }
        setSession(data);
        setSites(data.sites.map((s) => ({
          ...s,
          tou_windows: windowsToState(s.tou_windows),
          current_rates: s.current_rates || {},
          invoice_index: null,
          selected_tariff_id: null,
        })));
        (data.file_errors || []).forEach((e) => toast.warning(e.filename + ': ' + e.error));
        setStep(2);
        toast.success(data.sites.length + ' site(s) parsed, ' + data.sites.filter((s) => s.invoice).length + ' invoice(s) matched');
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Upload failed'))
      .finally(() => setUploading(false));
  };

  const updateSite = (nmi, patch) => setSites((prev) => prev.map((s) => (s.nmi === nmi ? { ...s, ...patch } : s)));
  const updateRates = (nmi, key, value) => setSites((prev) => prev.map((s) => (s.nmi === nmi ? { ...s, current_rates: { ...s.current_rates, [key]: value } } : s)));
  const updateWindow = (nmi, idx, patch) => setSites((prev) => prev.map((s) => {
    if (s.nmi !== nmi) return s;
    const w = [...s.tou_windows];
    w[idx] = { ...w[idx], ...patch };
    return { ...s, tou_windows: w };
  }));

  const assignInvoice = (nmi, idxStr) => {
    const idx = idxStr === 'none' ? null : parseInt(idxStr, 10);
    const inv = idx != null ? session.unmatched_invoices[idx] : null;
    const ex = inv?.extracted || {};
    updateSite(nmi, {
      invoice_index: idx,
      assigned_invoice_name: inv?.file_name || null,
      current_rates: idx != null ? {
        supply_charge_daily: ex.daily_supply_charge, usage_rate_flat: ex.usage_rate_flat,
        usage_rate_peak: ex.usage_rate_peak, usage_rate_shoulder: ex.usage_rate_shoulder,
        usage_rate_off_peak: ex.usage_rate_off_peak, demand_rate: ex.demand_rate, demand_unit: 'month',
      } : {},
    });
  };

  const runAnalysis = () => {
    setAnalysing(true);
    const payload = {
      sites: sites.map((s) => ({
        nmi: s.nmi,
        distributor: s.distributor || null,
        selected_tariff_id: s.selected_tariff_id && s.selected_tariff_id !== 'none' ? s.selected_tariff_id : null,
        current_rates: Object.fromEntries(Object.entries(s.current_rates || {}).map(([k, v]) => [k, v === '' || v == null ? null : (k === 'demand_unit' ? v : parseFloat(v))])),
        tou_windows: windowsToPayload(s.tou_windows),
        invoice_index: s.invoice_index,
      })),
    };
    axios.post(API_URL + '/api/optimisation/sessions/' + session.id + '/analyse?authorization=' + token, payload, { timeout: 120000 })
      .then((r) => { setResults(r.data.results); setStep(3); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Analysis failed'))
      .finally(() => setAnalysing(false));
  };

  const reset = () => { setStep(1); setFiles([]); setSession(null); setSites([]); setResults(null); };

  const siteTariffs = (site) => tariffs.filter((t) => {
    if (!t.network_distributor || !site.distributor) return false;
    const a = t.network_distributor.toLowerCase(); const b = site.distributor.toLowerCase();
    return a === b || a.includes(b) || b.includes(a);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="tariff-optimisation-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Zap className="w-6 h-6 text-[#8CC63F]" /> Tariff Optimisation</h1>
            <p className="text-slate-500 mt-1">Upload NEM12 interval data + customer invoices to find the cheapest SME tariff per site</p>
          </div>
          {step > 1 && <Button variant="outline" onClick={reset} data-testid="opt-reset-btn"><RotateCcw className="w-4 h-4 mr-2" />Start Over</Button>}
        </div>

        <StepHeader step={step} />

        {step === 1 && (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#8CC63F] bg-green-50' : 'border-slate-300 hover:border-[#015492]'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                data-testid="opt-dropzone"
              >
                <UploadCloud className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="font-medium text-slate-700">Drag & drop NEM12 / interval data files and customer invoices here</p>
                <p className="text-sm text-slate-500 mt-1">NEM12 & interval data: .csv / .txt / .dat — Invoices: .pdf / images. Multiple files supported.</p>
                <input ref={inputRef} type="file" multiple className="hidden" accept=".csv,.txt,.dat,.nem,.nem12,.mdf,.pdf,.jpg,.jpeg,.png,.webp,.tiff,.bmp" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="opt-file-input" />
              </div>

              {files.length > 0 && (
                <div className="space-y-2" data-testid="opt-file-list">
                  {files.map((f) => (
                    <div key={f.name} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      {isIntervalFile(f.name) ? <FileSpreadsheet className="w-5 h-5 text-green-600" /> : <FileText className="w-5 h-5 text-blue-600" />}
                      <span className="flex-1 text-sm text-slate-700 truncate">{f.name}</span>
                      <Badge variant="outline" className={isIntervalFile(f.name) ? 'text-green-700 border-green-300' : 'text-blue-700 border-blue-300'}>
                        {isIntervalFile(f.name) ? 'Interval Data' : 'Invoice'}
                      </Badge>
                      <button onClick={() => setFiles((prev) => prev.filter((x) => x.name !== f.name))} className="text-slate-400 hover:text-red-500" data-testid={`opt-remove-file-${f.name}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

              <Button onClick={handleUpload} disabled={uploading || !files.length} className="w-full bg-[#015492] hover:bg-[#013f6e]" data-testid="opt-upload-btn">
                {uploading ? (<><Clock className="w-4 h-4 mr-2 animate-spin" />Parsing files & extracting invoices (this can take a minute)...</>) : (<><UploadCloud className="w-4 h-4 mr-2" />Upload & Match ({files.length} file{files.length !== 1 ? 's' : ''})</>)}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {session?.unmatched_invoices?.length > 0 && (
              <Card className="border-amber-300 bg-amber-50" data-testid="opt-unmatched-invoices">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Unmatched Invoices</CardTitle>
                <CardDescription>These invoices could not be auto-matched to a site by NMI. Assign them below on the relevant site card.</CardDescription></CardHeader>
                <CardContent className="space-y-1">
                  {session.unmatched_invoices.map((inv, i) => (
                    <p key={i} className="text-sm text-slate-700">• {inv.file_name} {inv.extracted?.nmi ? `(NMI on invoice: ${inv.extracted.nmi})` : '(no NMI detected)'}</p>
                  ))}
                </CardContent>
              </Card>
            )}

            {sites.map((site) => (
              <Card key={site.nmi} data-testid={`opt-site-card-${site.nmi}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-lg">NMI: {site.nmi}</CardTitle>
                    {site.invoice ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle2 className="w-3 h-3 mr-1" />Invoice: {site.invoice.file_name}</Badge>
                    ) : site.assigned_invoice_name ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle2 className="w-3 h-3 mr-1" />Invoice: {site.assigned_invoice_name}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">No invoice matched</Badge>
                    )}
                    {site.files.map((f) => <Badge key={f} variant="outline" className="text-slate-500">{f}</Badge>)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[['Total Usage', fmtNum(site.profile.total_kwh) + ' kWh'], ['Data Days', site.profile.days + ' (' + site.profile.start_date + ' – ' + site.profile.end_date + ')'], ['Avg Daily', fmtNum(site.profile.avg_daily_kwh) + ' kWh'], ['Max Demand', site.profile.max_demand_kw + ' kW'], ['TOU Split', `P ${fmtNum(site.buckets?.peak)} / S ${fmtNum(site.buckets?.shoulder)} / OP ${fmtNum(site.buckets?.off_peak)}`]].map(([label, val]) => (
                      <div key={label} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                        <p className="text-sm font-semibold text-slate-800 mt-0.5">{val}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Distributor {site.detected_distributor && <span className="text-slate-400">(detected: {site.detected_distributor})</span>}</Label>
                      <Select value={site.distributor || ''} onValueChange={(v) => updateSite(site.nmi, { distributor: v, selected_tariff_id: null })}>
                        <SelectTrigger data-testid={`opt-distributor-select-${site.nmi}`}><SelectValue placeholder="Select distributor" /></SelectTrigger>
                        <SelectContent>
                          {[...new Set([...(site.detected_distributor ? [site.detected_distributor] : []), ...distributors])].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Current Network Tariff (identify from invoice)</Label>
                      <Select value={site.selected_tariff_id || 'none'} onValueChange={(v) => updateSite(site.nmi, { selected_tariff_id: v })}>
                        <SelectTrigger data-testid={`opt-tariff-select-${site.nmi}`}><SelectValue placeholder="Select tariff" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unknown / Not listed</SelectItem>
                          {siteTariffs(site).map((t) => <SelectItem key={t.id} value={t.id}>{t.tariff_code} — {t.tariff_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {session?.unmatched_invoices?.length > 0 && !site.invoice && (
                      <div>
                        <Label className="text-xs">Assign Invoice</Label>
                        <Select value={site.invoice_index != null ? String(site.invoice_index) : 'none'} onValueChange={(v) => assignInvoice(site.nmi, v)}>
                          <SelectTrigger data-testid={`opt-assign-invoice-${site.nmi}`}><SelectValue placeholder="Assign invoice" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {session.unmatched_invoices.map((inv, i) => <SelectItem key={i} value={String(i)}>{inv.file_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Current Pricing (from invoice — edit if needed)</p>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      {RATE_FIELDS.map((f) => (
                        <div key={f.key}>
                          <Label className="text-xs text-slate-500">{f.label}</Label>
                          <Input type="number" step="0.0001" value={site.current_rates?.[f.key] ?? ''} onChange={(e) => updateRates(site.nmi, f.key, e.target.value)} data-testid={`opt-rate-${f.key}-${site.nmi}`} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-[#015492] hover:underline" data-testid={`opt-tou-toggle-${site.nmi}`}>
                      <ChevronDown className="w-4 h-4" />TOU Windows ({site.distributor || 'default'} network timings — edit if needed)
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2">
                      {site.tou_windows.map((w, idx) => (
                        <div key={idx} className="flex flex-wrap items-center gap-2">
                          <Select value={w.period} onValueChange={(v) => updateWindow(site.nmi, idx, { period: v })}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="peak">Peak</SelectItem><SelectItem value="shoulder">Shoulder</SelectItem><SelectItem value="off_peak">Off-Peak</SelectItem></SelectContent>
                          </Select>
                          <Select value={w.days} onValueChange={(v) => updateWindow(site.nmi, idx, { days: v })}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="weekday">Weekdays</SelectItem><SelectItem value="weekend">Weekends</SelectItem><SelectItem value="all">All days</SelectItem></SelectContent>
                          </Select>
                          <Input type="number" min="0" max="24" className="w-20" value={w.start_hour} onChange={(e) => updateWindow(site.nmi, idx, { start_hour: e.target.value })} title="Start hour (0-24)" />
                          <span className="text-slate-400 text-sm">to</span>
                          <Input type="number" min="0" max="24" className="w-20" value={w.end_hour} onChange={(e) => updateWindow(site.nmi, idx, { end_hour: e.target.value })} title="End hour (0-24)" />
                          <Input placeholder="Months e.g. 11,12,1 (blank = all)" className="w-56" value={w.months} onChange={(e) => updateWindow(site.nmi, idx, { months: e.target.value })} />
                          <button onClick={() => updateSite(site.nmi, { tou_windows: site.tou_windows.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => updateSite(site.nmi, { tou_windows: [...site.tou_windows, { period: 'peak', days: 'weekday', start_hour: 14, end_hour: 20, months: '' }] })}><Plus className="w-3 h-3 mr-1" />Add Window</Button>
                      <p className="text-xs text-slate-400">Anything not covered by a window is billed Off-Peak. Hours use 24h format (e.g. 15 = 3pm).</p>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            ))}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
              <Button onClick={runAnalysis} disabled={analysing} className="flex-1 bg-[#8CC63F] hover:bg-[#7ab332] text-white" data-testid="opt-analyse-btn">
                {analysing ? (<><Clock className="w-4 h-4 mr-2 animate-spin" />Comparing against all {sites[0]?.distributor || ''} tariffs...</>) : (<><Zap className="w-4 h-4 mr-2" />Run Tariff Optimisation</>)}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && results && (
          <div className="space-y-5">
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="opt-back-review-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back to Review</Button>
              <Button
                className="bg-[#003366] hover:bg-[#002347]"
                onClick={() => { const a = document.createElement('a'); a.href = API_URL + '/api/optimisation/sessions/' + session.id + '/pdf?authorization=' + token; a.download = 'Tariff_Optimisation.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }}
                data-testid="opt-export-pdf-btn"
              ><Download className="w-4 h-4 mr-2" />Export PDF</Button>
            </div>

            {results.map((r) => (
              <Card key={r.nmi} data-testid={`opt-result-card-${r.nmi}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">NMI: {r.nmi} <span className="text-sm font-normal text-slate-500">— {r.distributor || 'Unknown distributor'}</span></CardTitle>
                  <CardDescription>
                    Annualised: <strong>{fmtNum(r.annual_kwh)} kWh</strong> (Peak {fmtNum(r.buckets_annual.peak)} / Shoulder {fmtNum(r.buckets_annual.shoulder)} / Off-Peak {fmtNum(r.buckets_annual.off_peak)}) • Avg max demand {r.avg_max_kw} kW
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {r.current?.total ? (
                    <div className="bg-slate-100 border-l-4 border-[#003366] rounded-r-lg p-4" data-testid={`opt-current-cost-${r.nmi}`}>
                      <p className="text-sm text-slate-500">Estimated current annual cost {r.current.basis === 'invoice_total' ? '(annualised from invoice total)' : ''}</p>
                      <p className="text-2xl font-bold text-[#003366]">{fmtMoney(r.current.total)}</p>
                      {r.current.basis === 'rates' && <p className="text-xs text-slate-500 mt-1">Supply {fmtMoney(r.current.supply_cost)} • Usage {fmtMoney(r.current.usage_cost)} • Demand {fmtMoney(r.current.demand_cost)}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600">No current pricing available — options ranked by estimated cost only.</p>
                  )}

                  {r.options.length === 0 ? (
                    <p className="text-sm text-red-600">No active tariffs found for distributor "{r.distributor}". Check the distributor selection or upload tariffs for this network.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-slate-500 uppercase border-b">
                            <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Tariff</th><th className="py-2 pr-2">Type</th>
                            <th className="py-2 pr-2">Supply</th><th className="py-2 pr-2">Usage</th><th className="py-2 pr-2">Demand</th>
                            <th className="py-2 pr-2">Est. Annual</th><th className="py-2 pr-2">Best Plan</th><th className="py-2">Savings vs Current</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.options.slice(0, 12).map((o, i) => (
                            <tr key={o.tariff_id} className={`border-b last:border-0 ${i === 0 ? 'bg-green-50' : o.is_current ? 'bg-blue-50' : ''}`} data-testid={`opt-option-row-${r.nmi}-${i}`}>
                              <td className="py-2 pr-2">{i === 0 ? <Badge className="bg-[#8CC63F] text-white">Best</Badge> : i + 1}</td>
                              <td className="py-2 pr-2"><span className="font-medium">{o.tariff_code}</span><br /><span className="text-xs text-slate-500">{o.tariff_name}</span>{o.is_current && <Badge variant="outline" className="ml-1 text-xs">Current</Badge>}</td>
                              <td className="py-2 pr-2 uppercase text-xs">{o.tariff_type}</td>
                              <td className="py-2 pr-2">{fmtMoney(o.supply_cost)}</td>
                              <td className="py-2 pr-2">{fmtMoney(o.usage_cost)}</td>
                              <td className="py-2 pr-2">{fmtMoney(o.demand_cost)}</td>
                              <td className="py-2 pr-2 font-semibold">{fmtMoney(o.total)}</td>
                              <td className="py-2 pr-2 text-xs">{o.best_plan_name ? <>{o.best_plan_name}<br /><span className="font-semibold text-slate-700">{fmtMoney(o.total_with_plan)}</span></> : '—'}</td>
                              <td className="py-2">{o.savings != null ? (
                                <span className={o.savings >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{fmtMoney(o.savings)} ({o.savings_pct}%)</span>
                              ) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-xs text-slate-400">{r.assumptions.join(' • ')}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
